// Hooks and utilities
import { useTranslation } from '@hooks/useTranslation';
import { cn } from '@/lib/utils';

// Types
import type { ApiEvent } from '@api/events';
import { resolveEventStatusKey } from './event-info-config';

export type EventFilter = 'all' | 'draft' | 'published' | 'cancelled';

const FILTER_KEYS: EventFilter[] = ['all', 'draft', 'published', 'cancelled'];

interface EventsFilterTabsProps {
  events: ApiEvent[];
  active: EventFilter;
  onChange: (filter: EventFilter) => void;
  className?: string;
}

export function EventsFilterTabs({ events, active, onChange, className }: EventsFilterTabsProps) {
  const { t } = useTranslation();

  const counts: Record<EventFilter, number> = {
    all: events.length,
    draft: 0,
    published: 0,
    cancelled: 0,
  };
  events.forEach((event) => {
    counts[resolveEventStatusKey(event.event_status)] += 1;
  });

  const labels: Record<EventFilter, string> = {
    all: t('events_section.filter_all'),
    draft: t('events_section.status.draft'),
    published: t('events_section.status.published'),
    cancelled: t('events_section.status.cancelled'),
  };

  return (
    <div role="tablist" className={cn('flex gap-1.5 overflow-x-auto', className)}>
      {FILTER_KEYS.map((filter) => {
        const selected = filter === active;

        return (
          <button
            key={filter}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(filter)}
            className={cn(
              'inline-flex items-center gap-1.5 min-h-12 shrink-0 rounded-full border border-dinnerplate-white bg-off-white px-4 font-inter text-sm font-semibold text-grey',
              selected && 'bg-dinnerplate-white-light text-off-black',
            )}
          >
            {labels[filter]}
            <span className="tabular-nums text-xs opacity-60">{counts[filter]}</span>
          </button>
        );
      })}
    </div>
  );
}
